import { cn } from '@/lib/utils'
import { forwardRef, ReactNode } from 'react'

export const H1 = forwardRef<
    HTMLHeadingElement,
    React.ComponentPropsWithoutRef<'h1'>
>(({ children, className, ...props }, ref) => {
    return (
        <h1
            ref={ref}
            {...props}
            className={cn(
                'font-brand font-medium tracking-brand scroll-m-20 text-4xl text-foreground lg:text-5xl',
                className,
            )}
        >
            {children}
        </h1>
    )
})
H1.displayName = 'H1'

export const H2 = forwardRef<
    HTMLHeadingElement,
    React.ComponentPropsWithoutRef<'h2'>
>(({ children, className, ...props }, ref) => {
    return (
        <h2
            ref={ref}
            {...props}
            className={cn(
                'scroll-m-20 pb-2 text-3xl font-semibold tracking-tight text-foreground first:mt-0',
                className,
            )}
        >
            {children}
        </h2>
    )
})
H2.displayName = 'H2'

export const H3 = forwardRef<
    HTMLHeadingElement,
    React.ComponentPropsWithoutRef<'h3'>
>(({ children, className, ...props }, ref) => {
    return (
        <h3
            ref={ref}
            {...props}
            className={cn(
                'scroll-m-20 text-2xl font-semibold tracking-tight text-foreground',
                className,
            )}
        >
            {children}
        </h3>
    )
})
H3.displayName = 'H3'

export const H4 = forwardRef<
    HTMLHeadingElement,
    React.ComponentPropsWithoutRef<'h4'>
>(({ children, className, ...props }, ref) => {
    return (
        <h4
            ref={ref}
            {...props}
            className={cn(
                'scroll-m-20 text-xl font-semibold tracking-tight text-foreground',
                className,
            )}
        >
            {children}
        </h4>
    )
})
H4.displayName = 'H4'

type ParagraphProps = React.ComponentPropsWithoutRef<'p'> & {
    noDefaultMargin?: boolean
}

export const Paragraph = forwardRef<HTMLParagraphElement, ParagraphProps>(
    ({ children, className, noDefaultMargin, ...props }, ref) => {
        return (
            <p
                ref={ref}
                {...props}
                className={cn(
                    'leading-7 text-foreground',
                    !noDefaultMargin && '[&:not(:first-child)]:mt-6',
                    className,
                )}
            >
                {children}
            </p>
        )
    },
)
Paragraph.displayName = 'Paragraph'

export const Blockquote = forwardRef<
    HTMLQuoteElement,
    React.ComponentPropsWithoutRef<'blockquote'>
>(({ children, className, ...props }, ref) => {
    return (
        <blockquote
            ref={ref}
            {...props}
            className={cn(
                'mt-6 border-l-2 border-border pl-6 italic text-foreground',
                className,
            )}
        >
            {children}
        </blockquote>
    )
})
Blockquote.displayName = 'Blockquote'

export const List = forwardRef<
    HTMLUListElement,
    React.ComponentPropsWithoutRef<'ul'>
>(({ children, className, ...props }, ref) => {
    return (
        <ul
            ref={ref}
            {...props}
            className={cn(
                'my-6 ml-6 list-disc text-foreground [&>li]:mt-2',
                className,
            )}
        >
            {children}
        </ul>
    )
})
List.displayName = 'List'

export const InlineCode = forwardRef<
    HTMLElement,
    React.ComponentPropsWithoutRef<'code'>
>(({ children, className, ...props }, ref) => {
    return (
        <code
            ref={ref}
            {...props}
            className={cn(
                'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold text-foreground',
                className,
            )}
        >
            {children}
        </code>
    )
})
InlineCode.displayName = 'InlineCode'

export const Lead = forwardRef<
    HTMLParagraphElement,
    React.ComponentPropsWithoutRef<'p'>
>(({ children, className, ...props }, ref) => {
    return (
        <p
            ref={ref}
            {...props}
            className={cn('text-xl text-muted-foreground', className)}
        >
            {children}
        </p>
    )
})
Lead.displayName = 'Lead'

export const Large = forwardRef<
    HTMLDivElement,
    React.ComponentPropsWithoutRef<'div'>
>(({ children, className, ...props }, ref) => {
    return (
        <div
            ref={ref}
            {...props}
            className={cn('text-lg font-semibold text-foreground', className)}
        >
            {children}
        </div>
    )
})
Large.displayName = 'Large'

export const Medium = forwardRef<
    HTMLDivElement,
    React.ComponentPropsWithoutRef<'div'>
>(({ children, className, ...props }, ref) => {
    return (
        <div
            ref={ref}
            {...props}
            className={cn('text-md font-normal text-foreground', className)}
        >
            {children}
        </div>
    )
})
Medium.displayName = 'Medium'

export const Small = forwardRef<
    HTMLElement,
    React.ComponentPropsWithoutRef<'small'>
>(({ children, className, ...props }, ref) => {
    return (
        <small
            ref={ref}
            {...props}
            className={cn(
                'text-sm font-medium leading-none text-foreground',
                className,
            )}
        >
            {children}
        </small>
    )
})
Small.displayName = 'Small'

export const XSmall = forwardRef<
    HTMLElement,
    React.ComponentPropsWithoutRef<'small'>
>(({ children, className, ...props }, ref) => {
    return (
        <small
            ref={ref}
            {...props}
            className={cn('text-xs leading-none text-foreground', className)}
        >
            {children}
        </small>
    )
})
XSmall.displayName = 'XSmall'

export const Muted = forwardRef<
    HTMLParagraphElement,
    React.ComponentPropsWithoutRef<'p'> & { children: ReactNode }
>(({ children, className, ...props }, ref) => {
    return (
        <p
            ref={ref}
            {...props}
            className={cn('text-sm text-muted-foreground', className)}
        >
            {children}
        </p>
    )
})
Muted.displayName = 'Muted'

type TableProps = React.ComponentPropsWithoutRef<'div'> & {
    headers?: string[]
    rows?: string[][]
}

export const Table = forwardRef<HTMLDivElement, TableProps>(
    ({ children, className, headers, rows, ...props }, ref) => {
        return (
            <div
                ref={ref}
                {...props}
                className={cn('my-6 w-full overflow-y-auto', className)}
            >
                <table className="w-full text-foreground">
                    {headers && (
                        <thead>
                            <tr className="m-0 border-t p-0 border-border even:bg-muted">
                                {headers.map((header, index) => (
                                    <th
                                        key={index}
                                        className="border px-4 py-2 text-left font-bold border-border [&[align=center]]:text-center [&[align=right]]:text-right"
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                    )}
                    {rows && (
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr
                                    key={rowIndex}
                                    className="m-0 border-t p-0 border-border even:bg-muted"
                                >
                                    {row.map((cell, cellIndex) => (
                                        <td
                                            key={cellIndex}
                                            className="border px-4 py-2 text-left border-border [&[align=center]]:text-center [&[align=right]]:text-right"
                                        >
                                            {cell}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    )}
                    {children && !rows && <tbody>{children}</tbody>}
                </table>
            </div>
        )
    },
)
Table.displayName = 'Table'

export function TypographyDemo() {
    return (
        <div className="text-foreground">
            <H1>The Joke Tax Chronicles</H1>
            <Paragraph>
                Once upon a time, in a far-off land, there was a very lazy king
                who spent all day lounging on his throne. One day, his advisors
                came to him with a problem: the kingdom was running out of
                money.
            </Paragraph>
            <H2>The King&apos;s Plan</H2>
            <Paragraph>
                The king thought long and hard, and finally came up with{' '}
                <a
                    href="#"
                    className="font-medium underline underline-offset-4 text-primary"
                >
                    a brilliant plan
                </a>
                : he would tax the jokes in the kingdom.
            </Paragraph>
            <Blockquote>
                &quot;After all,&quot; he said, &quot;everyone enjoys a good
                joke, so it&apos;s only fair that they should pay for the
                privilege.&quot;
            </Blockquote>
            <H3>The Joke Tax</H3>
            <Paragraph>
                The king&apos;s subjects were not amused. They grumbled and
                complained, but the king was firm:
            </Paragraph>
            <List>
                <li>1st level of puns: 5 gold coins</li>
                <li>2nd level of jokes: 10 gold coins</li>
                <li>3rd level of one-liners : 20 gold coins</li>
            </List>
        </div>
    )
}
